import React, { useMemo, useState, useEffect, useRef } from "react";
import { Box, Grid, Typography, Card, CardContent, Chip, Avatar, Skeleton, Divider } from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import { orange, blue, green, purple, yellow, common } from "@/theme/colors";
import { buildOrganizationRouteId } from "@/utils/orgRoutes";
import { doesRegionLabelMatchSelection, doesRegionMatchPrefs, getContentCountsCache } from "@/utils/localStore";
import SaveButton from "@/components/Importable/SaveButton";
import { useAuth } from "@/context/AuthContext";
import { useGetOrganizationContentCountsQuery } from "@/src/services/organizationsApi";
import { collapseRegionsForDisplay } from "@/components/Importable/Pickers";
import SafeImage from "@/components/Importable/SafeImage";
import { isRenderableImageSrc } from "@/utils/imageSrc";
import { cardActivationProps } from "@/utils/cardA11y";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const OrganizationCard = styled(Card)(({
  theme
}) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  // Layout only — elevation, border, transition, and the professionals-
  // style hover treatment live on the instances' sx so the saved-state
  // border semantics are defined in exactly one place.
  cursor: "pointer",
  width: "100%",
  maxWidth: 425,
  [theme.breakpoints.down("sm")]: {
    maxWidth: 390
  },
  "@media (max-width: 375px)": {
    maxWidth: 370
  }
}));
const CardHeaderBox = styled(Box)(({
  theme
}) => ({
  padding: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2)
}));
const OrganizationLogo = styled(Avatar)(({
  theme
}) => ({
  width: 72,
  height: 72,
  // Square logo with a subtle 3px radius — override the Avatar's default
  // circular variant (which otherwise wins and makes the logo round).
  borderRadius: 3,
  "&.MuiAvatar-root": {
    borderRadius: 3
  },
  // White (matching the card/page) so transparent-background logos don't
  // sit on a mismatched gray tile.
  backgroundColor: theme.palette.common.white
}));
const TagContainer = styled(Box)(({
  theme
}) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(1)
}));
const truncate = (text, max = 350) => {
  if (!text) return "";
  const clean = String(text);
  return clean.length > max ? `${clean.slice(0, max).trimEnd()}…` : clean;
};

/* ── Individual org card with local saved state ─────────────────────────────── */

const OrgCardItemInner = ({
  org,
  routeSlug,
  mounted,
  filterRegion,
  filterDiscipline,
  filterProduct,
  tagPrefs,
  onOrganizationClick,
  contentCounts,
  viewMode
}) => {
  const {
    savedItems,
    isAuthenticated
  } = useAuth();
  const rawDescription = org.description && org.description.trim() ? org.description : org.orgDescrip && org.orgDescrip.trim() ? org.orgDescrip : "";
  // Full-width list rows fit far more text: prefer the long About-Us
  // body (orgDescrip) there; grid keeps the short blurb.
  const longDescription = org.orgDescrip && org.orgDescrip.trim() ? org.orgDescrip : rawDescription;
  const displayDescription = (viewMode === "list" ? longDescription : rawDescription) || org.orgName;
  const employeesLabel = (org.employees || "").trim();

  // Derive saved state from context — no API calls, no local state
  const normalizedId = String(org.backendId || org.id || org.slug || "").trim().toLowerCase();
  const normalizedSlug = String(org.slug || "").trim().toLowerCase();
  const isSaved = useMemo(() => {
    if (!isAuthenticated || !normalizedId) return false;
    return savedItems.some(item => item.resourceType === "organization" && (item.resourceId === normalizedId || normalizedSlug && item.resourceSlug === normalizedSlug));
  }, [isAuthenticated, normalizedId, normalizedSlug, savedItems]);
  const savedToggleNode = /*#__PURE__*/_jsx(SaveButton, {
    resourceType: "organization",
    resourceId: org.backendId || org.id || org.slug,
    resourceSlug: org.slug,
    resourceTitle: org.orgName,
    resourceSummary: org.orgDescrip || org.description || org.orgName,
    resourceImage: org.logoUrl || null,
    color: orange.primary
  });

  // Placeholder paths, "/" from assetUrl(""), and URLs that 404 all
  // painted the browser's broken-image icon here — SafeImage renders
  // nothing instead (see utils/imageSrc).
  const logoNode = isRenderableImageSrc(org.logoUrl) ? /*#__PURE__*/_jsx(OrganizationLogo, {
    children: /*#__PURE__*/_jsx(SafeImage, {
      src: org.logoUrl,
      alt: `${org.orgName} logo`,
      width: 60,
      height: 60,
      sx: {
        width: "100%",
        height: "100%",
        objectFit: "cover"
      }
    })
  }) : null;

  // Shrink the org name to fit on one line in the space left of the corner
  // save button (instead of wrapping). Measured with a stable transform —
  // the span's scrollWidth is read at the base font and transforms don't
  // affect it, so there's no measure→resize feedback loop.
  const nameTextRef = useRef(null);
  const [nameScale, setNameScale] = useState(1);
  useEffect(() => {
    const span = nameTextRef.current;
    const box = span?.parentElement;
    if (!span || !box) return;
    // px the corner save button occupies past the header's right edge,
    // plus a little gap (button ~40px wide, inset 3px from a 16px-padded
    // header).
    const SAVE_BTN_RESERVE = 34;
    const fit = () => {
      const available = box.clientWidth - SAVE_BTN_RESERVE;
      const natural = span.scrollWidth;
      setNameScale(natural > 0 && available > 0 ? Math.max(0.7, Math.min(1, available / natural)) : 1);
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(box);
    return () => ro.disconnect();
  }, [org.orgName]);
  const nameNode = /*#__PURE__*/_jsx(Typography, {
    variant: "h6",
    component: "h2",
    sx: {
      whiteSpace: "nowrap",
      overflow: "hidden"
    },
    children: /*#__PURE__*/_jsx(Box, {
      component: "span",
      ref: nameTextRef,
      sx: {
        display: "inline-block",
        transformOrigin: "left center",
        transform: nameScale < 1 ? `scale(${nameScale})` : "none"
      },
      children: org.orgName
    })
  });
  const employeesNode = null; /* {employeesLabel ? (
                              <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block", mt: 0.75 }}
                              >
                              Employees: {employeesLabel}
                              </Typography>
                              ) : null} */

  const locationNode = (() => {
    const loc = org.infoSection?.location || {};
    const parts = [];
    if (loc.address) parts.push(loc.address);
    if (loc.address2) parts.push(loc.address2);
    if (loc.city) parts.push(loc.city);
    const cityLower = String(loc.city || "").toLowerCase();
    if (loc.state && !cityLower.includes(String(loc.state).toLowerCase())) parts.push(loc.state);
    if (loc.postalCode && !cityLower.includes(String(loc.postalCode).toLowerCase())) parts.push(loc.postalCode);
    if (loc.country && !cityLower.includes(String(loc.country).toLowerCase())) parts.push(loc.country);
    const rendered = parts.filter(Boolean).join(", ");
    return /*#__PURE__*/_jsx(Typography, {
      variant: "body2",
      color: "text.secondary",
      sx: {
        display: "block",
        mt: 0.5,
        wordBreak: "break-word"
      },
      title: rendered,
      children: rendered || loc.city || ""
    });
  })();
  const descriptionNode = /*#__PURE__*/_jsx(Typography, {
    variant: "body2",
    color: "text.secondary",
    paragraph: true,
    sx: {
      lineHeight: 1.5,
      display: "-webkit-box",
      // Dynamic safe area: the clamp alone sets the height (the
      // old fixed 7.2em maxHeight could slice a line mid-height),
      // and list rows earn more lines as the viewport widens.
      WebkitLineClamp: viewMode === "list" ? {
        xs: 4,
        sm: 5,
        md: 6
      } : 4,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      textOverflow: "ellipsis",
      wordBreak: "break-word"
    },
    title: displayDescription,
    children: truncate(displayDescription, viewMode === "list" ? 1200 : 350)
  });
  const countsNode = /*#__PURE__*/_jsx(Box, {
    sx: {
      mb: 2
    },
    children: mounted && (() => {
      const parts = [];
      const eventsCount = contentCounts?.events || org.events?.length || 0;
      const regsCount = contentCounts?.regulations || org.regulations?.length || 0;
      const articlesCount = contentCounts?.articles || 0;
      const servicesCount = contentCounts?.services || org.services?.length || 0;
      if (eventsCount > 0) parts.push(`Events: ${eventsCount}`);
      if (regsCount > 0) parts.push(`Regulations: ${regsCount}`);
      if (articlesCount > 0) parts.push(`Articles: ${articlesCount}`);
      if (servicesCount > 0) parts.push(`Services: ${servicesCount}`);
      if (parts.length === 0) return null;
      return /*#__PURE__*/_jsx(Typography, {
        variant: "caption",
        color: "text.secondary",
        children: parts.join(" • ")
      });
    })()
  });
  const tagChips = /*#__PURE__*/_jsxs(_Fragment, {
    children: [collapseRegionsForDisplay(org.regionalFocus || []).map(region => {
      const isActive = filterRegion && doesRegionLabelMatchSelection(region, filterRegion) || doesRegionMatchPrefs(region, tagPrefs.regions);
      return /*#__PURE__*/_jsx(Chip, {
        label: region,
        size: "small",
        variant: isActive ? "filled" : "outlined",
        sx: {
          borderColor: purple.primary,
          color: isActive ? common.white : purple.primary,
          backgroundColor: isActive ? purple.primary : "transparent",
          fontWeight: 600,
          height: "auto",
          "& .MuiChip-label": {
            whiteSpace: "normal",
            display: "block",
            lineHeight: 1.3,
            py: "3px"
          }
        }
      }, `region-${region}`);
    }), (org.orgFocus || []).map(disc => {
      const isActive = filterDiscipline && String(disc || "").toLowerCase() === filterDiscipline.toLowerCase() || tagPrefs.disciplines.some(d => d.toLowerCase() === String(disc || "").toLowerCase());
      return /*#__PURE__*/_jsx(Chip, {
        label: disc,
        size: "small",
        variant: isActive ? "filled" : "outlined",
        sx: {
          borderColor: green.primary,
          color: isActive ? common.white : green.primary,
          backgroundColor: isActive ? green.primary : "transparent",
          fontWeight: 600
        }
      }, `disc-${disc}`);
    }), (org.productFocus || []).map(prod => {
      const isActive = filterProduct && String(prod || "").toLowerCase() === filterProduct.toLowerCase() || tagPrefs.products.some(p => p.toLowerCase() === String(prod || "").toLowerCase());
      return /*#__PURE__*/_jsx(Chip, {
        label: prod,
        size: "small",
        variant: isActive ? "filled" : "outlined",
        sx: {
          borderColor: blue.primary,
          color: isActive ? common.white : blue.primary,
          backgroundColor: isActive ? blue.primary : "transparent",
          fontWeight: 600
        }
      }, `prod-${prod}`);
    })]
  });

  // ── List view: full-width row with image-left, info-right layout ──────
  if (viewMode === "list") {
    const listImage = org.bannerImages?.[0] || "";
    return /*#__PURE__*/_jsx(Grid, {
      size: {
        xs: 12
      },
      sx: {
        display: "flex",
        // Skip layout/paint for off-screen cards.
        contentVisibility: "auto",
        containIntrinsicSize: "320px 320px"
      },
      children: /*#__PURE__*/_jsxs(Card, {
        ...cardActivationProps(`Open ${org.orgName}`, () => onOrganizationClick(org)),
        sx: theme => ({
          position: "relative",
          width: "100%",
          display: "flex",
          flexDirection: {
            xs: "column",
            sm: "row"
          },
          cursor: "pointer",
          overflow: "hidden",
          border: isSaved ? `2px solid ${orange.primary}` : "2px solid transparent",
          boxShadow: theme.shadows[2],
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: theme.shadows[10],
            borderColor: isSaved ? orange.primary : alpha(orange.primary, 0.5)
          }
        }),
        children: [savedToggleNode, /*#__PURE__*/_jsx(Box, {
          sx: {
            flexShrink: 0,
            width: {
              xs: "100%",
              sm: 300
            },
            maxWidth: {
              sm: 300
            },
            height: {
              xs: 300,
              sm: 300
            },
            maxHeight: 300,
            aspectRatio: "1 / 1",
            backgroundColor: theme => theme.palette.common.white,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden"
          },
          children: (() => {
            const initial = /*#__PURE__*/_jsx(Avatar, {
              sx: {
                width: 80,
                height: 80
              },
              children: org.orgName?.charAt(0) || "?"
            });
            // Banner first, then logo, then the initial —
            // a broken banner now falls through instead of
            // leaving a broken-image icon.
            if (isRenderableImageSrc(listImage)) {
              return /*#__PURE__*/_jsx(SafeImage, {
                src: listImage,
                alt: `${org.orgName} banner`,
                fallback: initial,
                sx: {
                  width: "100%",
                  height: "100%",
                  objectFit: "cover"
                }
              });
            }
            if (isRenderableImageSrc(org.logoUrl)) {
              return /*#__PURE__*/_jsx(SafeImage, {
                src: org.logoUrl,
                alt: `${org.orgName} logo`,
                fallback: initial,
                width: 120,
                height: 120,
                sx: {
                  maxWidth: "80%",
                  maxHeight: "80%",
                  objectFit: "contain"
                }
              });
            }
            return initial;
          })()
        }), /*#__PURE__*/_jsxs(CardContent, {
          sx: {
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 1
          },
          children: [/*#__PURE__*/_jsxs(Box, {
            children: [nameNode, employeesNode, locationNode]
          }), descriptionNode, countsNode, /*#__PURE__*/_jsx(TagContainer, {
            children: tagChips
          })]
        })]
      })
    }, routeSlug || org.id || org.orgName);
  }
  return /*#__PURE__*/_jsx(Grid, {
    size: {
      xs: 12,
      sm: 6,
      md: 6,
      lg: 4
    },
    sx: {
      display: "flex",
      justifyContent: "center",
      // Skip layout/paint for off-screen cards.
      contentVisibility: "auto",
      containIntrinsicSize: "460px 460px"
    },
    children: /*#__PURE__*/_jsxs(OrganizationCard, {
      ...cardActivationProps(`Open ${org.orgName}`, () => onOrganizationClick(org)),
      sx: theme => ({
        position: "relative",
        border: isSaved ? `2px solid ${orange.primary}` : "2px solid transparent",
        boxShadow: theme.shadows[2],
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: theme.shadows[10],
          borderColor: isSaved ? orange.primary : alpha(orange.primary, 0.5)
        }
      }),
      children: [savedToggleNode, /*#__PURE__*/_jsxs(CardHeaderBox, {
        children: [logoNode, /*#__PURE__*/_jsxs(Box, {
          sx: {
            flexGrow: 1,
            minWidth: 0
          },
          children: [nameNode, employeesNode, locationNode]
        })]
      }), /*#__PURE__*/_jsxs(CardContent, {
        sx: {
          flexGrow: 1,
          pt: 0,
          display: "flex",
          flexDirection: "column"
        },
        children: [descriptionNode, countsNode, /*#__PURE__*/_jsx(TagContainer, {
          sx: {
            mt: "auto"
          },
          children: tagChips
        })]
      })]
    })
  }, routeSlug || org.id || org.orgName);
};

/** Memoized: streaming reveal re-renders the section once per chunk —
 *  already-visible cards must not re-render each frame. */
const OrgCardItem = /*#__PURE__*/React.memo(OrgCardItemInner);

/* ── Results section ────────────────────────────────────────────────────────── */

const ResultsSection = ({
  mostRelevant,
  remainingResults,
  showRelevanceSection,
  allOrgs,
  organizations,
  filterRegion,
  filterDiscipline,
  filterProduct,
  tagPrefs,
  onOrganizationClick,
  loading = false,
  emptyState,
  viewMode = "grid"
}) => {
  const [cachedCounts] = useState(() => getContentCountsCache());
  // Single page-level mounted flag for every card (SSR-safe client gates).
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  // Route ids in ONE pass per list change (collision detection scans the
  // whole list per org — doing it per card per render was O(n²) every
  // streaming frame).
  const routeIds = useMemo(() => {
    const existing = allOrgs.length ? allOrgs : organizations;
    const m = new Map();
    for (const o of [...mostRelevant, ...remainingResults]) {
      m.set(o, buildOrganizationRouteId(o, {
        existing
      }));
    }
    return m;
  }, [allOrgs, organizations, mostRelevant, remainingResults]);
  const {
    data: fetchedCounts
  } = useGetOrganizationContentCountsQuery(undefined, {
    skip: !!cachedCounts
  });
  const contentCountsMap = fetchedCounts ?? cachedCounts;
  const renderOrgCard = org => {
    const orgBackendId = org.backendId || "";
    const counts = orgBackendId ? contentCountsMap?.[orgBackendId] : undefined;
    const routeSlug = routeIds.get(org) ?? "";
    return /*#__PURE__*/_jsx(OrgCardItem, {
      org: org,
      routeSlug: routeSlug,
      mounted: mounted,
      filterRegion: filterRegion,
      filterDiscipline: filterDiscipline,
      filterProduct: filterProduct,
      tagPrefs: tagPrefs,
      onOrganizationClick: onOrganizationClick,
      contentCounts: counts,
      viewMode: viewMode
    }, routeSlug || org.id || org.orgName);
  };
  const renderSkeletons = () => /*#__PURE__*/_jsx(Grid, {
    "aria-label": "Organization Card Grid",
    container: true,
    spacing: {
      xs: 2.5,
      md: 3
    },
    sx: {
      justifyContent: {
        xs: "center",
        md: "center"
      }
    },
    children: Array.from({
      length: 9
    }).map((_, idx) => /*#__PURE__*/_jsx(Grid, {
      size: {
        xs: 12,
        sm: 6,
        md: 6,
        lg: 4
      },
      sx: {
        display: "flex",
        justifyContent: "center"
      },
      children: /*#__PURE__*/_jsxs(OrganizationCard, {
        sx: theme => ({
          pointerEvents: "none",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[1],
          "&:hover": {
            transform: "none",
            boxShadow: theme.shadows[1]
          }
        }),
        children: [/*#__PURE__*/_jsxs(CardHeaderBox, {
          children: [/*#__PURE__*/_jsx(Skeleton, {
            variant: "circular",
            width: 72,
            height: 72
          }), /*#__PURE__*/_jsxs(Box, {
            sx: {
              flexGrow: 1,
              minWidth: 0
            },
            children: [/*#__PURE__*/_jsx(Skeleton, {
              variant: "text",
              width: "70%",
              height: 28
            }), /*#__PURE__*/_jsx(Skeleton, {
              variant: "text",
              width: "40%",
              height: 18
            }), /*#__PURE__*/_jsx(Skeleton, {
              variant: "text",
              width: "55%",
              height: 18
            })]
          })]
        }), /*#__PURE__*/_jsxs(CardContent, {
          sx: {
            flexGrow: 1,
            pt: 0,
            display: "flex",
            flexDirection: "column"
          },
          children: [/*#__PURE__*/_jsx(Skeleton, {
            variant: "rectangular",
            height: 100,
            sx: {
              borderRadius: 1,
              mb: 2
            }
          }), /*#__PURE__*/_jsx(Skeleton, {
            variant: "text",
            height: 18,
            width: "85%",
            sx: {
              mb: 1
            }
          }), /*#__PURE__*/_jsx(Skeleton, {
            variant: "text",
            height: 18,
            width: "70%",
            sx: {
              mb: 3
            }
          }), /*#__PURE__*/_jsx(Box, {
            sx: {
              display: "flex",
              flexWrap: "wrap",
              gap: 0.75,
              mt: "auto"
            },
            children: Array.from({
              length: 4
            }).map((__, chipIdx) => /*#__PURE__*/_jsx(Skeleton, {
              variant: "rounded",
              height: 24,
              width: 80
            }, `org-skeleton-chip-${idx}-${chipIdx}`))
          })]
        })]
      })
    }, `org-skeleton-${idx}`))
  });
  if (loading) {
    return renderSkeletons();
  }
  return /*#__PURE__*/_jsxs(Box, {
    "aria-label": "Organization Card Grid",
    "data-select-target": "Organization Card Grid",
    children: [mostRelevant.length === 0 && remainingResults.length === 0 ? /*#__PURE__*/_jsxs(Box, {
      sx: {
        textAlign: "center",
        py: 8,
        px: 2
      },
      children: [/*#__PURE__*/_jsx(Typography, {
        variant: "h6",
        color: "text.secondary",
        sx: {
          mb: 1
        },
        children: "No results found"
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "body2",
        color: "text.secondary",
        children: "Please refine your search or filter choices to view different results."
      })]
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [showRelevanceSection && /*#__PURE__*/_jsxs(Box, {
        "aria-label": "Most Relevant Organizations",
        sx: {
          borderRadius: 2,
          // Organizations gold: solid border, same hue
          // at 0.3 alpha for the fill.
          backgroundColor: alpha(yellow.primary, 0.3),
          border: "1px solid",
          borderColor: yellow.primary,
          p: {
            xs: 2,
            md: 3
          },
          mb: 3,
          width: "100%"
        },
        children: [/*#__PURE__*/_jsx(Typography, {
          variant: "h6",
          sx: {
            fontWeight: 600,
            mb: 2,
            width: "100%"
          },
          children: "Most relevant to you"
        }), /*#__PURE__*/_jsx(Grid, {
          "aria-label": "Organization Card Grid",
          container: true,
          spacing: {
            xs: 2.5,
            md: 3
          },
          sx: {
            justifyContent: {
              xs: "center",
              md: "center"
            }
          },
          children: mostRelevant.map(renderOrgCard)
        })]
      }), !showRelevanceSection && /*#__PURE__*/_jsx(Grid, {
        "aria-label": "Organization Card Grid",
        container: true,
        spacing: {
          xs: 2.5,
          md: 3
        },
        sx: {
          justifyContent: {
            xs: "center",
            md: "center"
          }
        },
        children: mostRelevant.map(renderOrgCard)
      }), showRelevanceSection && remainingResults.length > 0 && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Divider, {
          sx: {
            width: "100%",
            my: 4
          },
          children: /*#__PURE__*/_jsx(Typography, {
            variant: "body2",
            color: "text.secondary",
            children: "Remaining results"
          })
        }), /*#__PURE__*/_jsx(Grid, {
          "aria-label": "Remaining Organizations",
          "data-select-target": "Remaining Organizations",
          container: true,
          spacing: {
            xs: 2.5,
            md: 3
          },
          sx: {
            justifyContent: {
              xs: "center",
              md: "center"
            }
          },
          children: remainingResults.map(renderOrgCard)
        })]
      })]
    }), emptyState]
  });
};
export default ResultsSection;